import { css } from '@emotion/css';
import { createSignal, For, Show } from 'solid-js';
import type { JSX } from 'solid-js';
import { ColourPicker } from 'Shared/forms/ColourPicker';
import { integerStyle, selectStyle as fieldSelectStyle, inputStyle } from 'Shared/forms/Inputs';
import { replaceDoc } from 'Shared/forms/SubdocOps';
import { StoreProvider, useStore } from 'Shared/StoreProvider';
import { LabelOnLeftWrap } from 'Shared/forms/Wrap';
import { subdoc } from 'Common/ViewUtils';
import { resolveColour } from 'Shared/model/settingColour';
import { SettingColour, Palette } from 'Shared/model/venueSettings';
import { useVenue } from 'Shared/VenueProvider';
import { backendTheme } from 'Shared/backend/Theme';


/*
	Nicer labels for the palette entries a setting can point at. Any key not listed - an additional
	colour the venue added on the settings page - is shown under its own name.
*/
export const paletteLabels: Record<string,string> = {
	background:    'Background',
	text:          'Text',
	dimBackground: 'Dim background',
	feature:       'Feature',
	white:         'White',
	dimFeature:    'Dim feature',
	inverseText:   'Inverse text',
	feature2:      'Feature 2',
	feature3:      'Feature 3',
	food:          'Food',
	drinks:        'Drinks',
	cactus:        'Cactus',
};

/*
	How a source key reads in the dropdown. Every palette entry is offered, then the two escapes from
	the palette - 'literal' (a colour of the venue's own) and 'none' (unset).
*/
function sourceLabel(key:string): string
{
	if (key == 'none')
		return 'Not set';
	if (key == 'literal')
		return 'Custom';
	return paletteLabels[key] ?? key;
}


const rowStyle = css({
	display:    'flex',
	alignItems: 'center',
	gap:        '0.6em',
	flexWrap:   'wrap',
});

/* A <select>-can't-do-this dropdown: the trigger keeps the old field's underline look, the panel is
   a hand-built list so each palette colour can carry a swatch. Built like Shared/forms/DrillDownSelect. */
const sourceWrapStyle = css({
	position: 'relative',
	display:  'inline-block',
});

const sourceTriggerStyle = css(fieldSelectStyle(), {
	display:      'inline-flex',
	alignItems:   'center',
	gap:          '0.4em',
	minWidth:     170,
	borderRadius: 0,
	textAlign:    'left',
	cursor:       'pointer',
});

const sourcePanelStyle = css({
	position:     'absolute',
	top:          '100%',
	left:         0,
	zIndex:       20,
	marginTop:    2,
	minWidth:     '100%',
	maxHeight:    '60vh',
	overflowY:    'auto',
	display:      'flex',
	flexDirection:'column',
	background:   'white',
	border:       '1px solid #ccc',
	borderRadius: 3,
	boxShadow:    '0 4px 20px rgba(0,0,0,0.25)',

	hr: {
		width:     '100%',
		margin:    '0.25em 0',
		border:    'none',
		borderTop: '1px solid #ddd',
	},
});

const sourceRowStyle = css({
	display:    'flex',
	alignItems: 'center',
	gap:        '0.5em',
	padding:    '0.4em 0.6em',
	background: 'none',
	border:     'none',
	font:       'inherit',
	textAlign:  'left',
	cursor:     'pointer',

	'&:hover': {backgroundColor: backendTheme().palette.dimBackground},
});

const sourceRowCurrentStyle = css({
	fontWeight: 'bold',
});

const dotStyle = css({
	width:        14,
	height:       14,
	borderRadius: '50%',
	border:       '1px solid #888',
	flexShrink:   0,
	boxSizing:    'border-box',
});

const dotEmptyStyle = css({
	backgroundImage: 'repeating-conic-gradient(#bbb 0% 25%, #eee 0% 50%)',
	backgroundSize:  '6px 6px',
});

const dotIconStyle = css({
	width:          14,
	flexShrink:     0,
	display:        'flex',
	justifyContent: 'center',
	color:          '#888',
	fontSize:       11,
});

const caretStyle = css({
	marginLeft: 'auto',
	color:      '#888',
	fontSize:   '0.8em',
});

const swatchStyle = css({
	width:           26,
	height:          26,
	borderRadius:    5,
	border:          '2px solid #444',
	flexShrink:      0,
	backgroundImage: 'repeating-conic-gradient(#888 0% 25%, #555 0% 50%)',
	backgroundSize:  '8px 8px',
});

const swatchColourStyle = css({
	width:        '100%',
	height:       '100%',
	borderRadius: 3,
});

const notesStyle = css({
	fontSize:   '0.8em',
	lineHeight: '1.2em',
	color:      '#4d4d4d',
});

const percentageStyle = () => css(inputStyle(false,false), {
	width: '4em',
	textAlign: 'right'
});


/*
	One colour in the frontend settings tree.

	A leaf is a discriminated union rather than a fixed-shape object, so switching what kind of colour
	it is (palette link, literal, or unset) replaces the whole leaf rather than patching a sub-field.
	The controls that edit it live under a store rooted at the leaf itself.
*/
export function StoreColourSetting(P:{field:string, label:string, notes:string}): JSX.Element
{
	const sw = useStore();

	return (
		<LabelOnLeftWrap label={P.label} narrow={true}>
			<StoreProvider {...sw} location={[...sw.location,P.field]}>
				<ColourSettingControls notes={P.notes} />
			</StoreProvider>
		</LabelOnLeftWrap>
	);
}

/* The control on its own, for a caller that has its own label or sits in a grid of its own - see
   BorderSettings, whose colour is one cell of a four-column row. */
export function ColourSettingControls(P:{notes:string}): JSX.Element
{
	const sw = useStore();
	const venue = useVenue();

	/* The venue's palette. Read off the live store when this control sits on a store rooted at the
	   venue document (the settings page) so a colour added there shows up in the select straight
	   away; otherwise - eg the style-params editor in the page/component editor, whose store is the
	   page document - fall back to the venue the provider carries. */
	const palette = () =>
		(subdoc(sw.store,['settings','frontend','palette'])
			?? venue?.settings?.frontend?.palette
			?? {}) as Palette;

	/* Absent both before the venue has been through the nav-colour migration and whenever the venue
	   hasn't styled this slot - a missing leaf shouldn't take the whole settings page down with it.
	   A bare string is a colour stored before palette links existed (see seedCustomStyleColours);
	   normalise it to a literal so the rest of the control has one shape to reason about. */
	const stored = () => subdoc(sw.store,sw.location) as SettingColour|string|undefined;
	const colour = () => {
		const c = stored();
		return typeof c=='string' ? {literal:c} as SettingColour : c;
	};

	/* One select covers both 'which palette entry' and the two escapes from the palette. */
	const selected = () => {
		const c = colour();
		return c==undefined ? 'none' : 'palette' in c ? c.palette : 'literal';
	};

	const literalValue = () => {
		const c = colour();
		return c!=undefined && 'literal' in c ? c.literal : '';
	};

	const isPaletteColour = () => {
		const c = colour();
		return c!=undefined && 'palette' in c;
	};

	const brightnessAdjustment = () => {
		const c = colour();
		return c!=undefined && 'palette' in c ? c.brightnessAdjustment ?? 0 : 0;
	};

	const setSource = async (choice:string) => {
		const carried = brightnessAdjustment();

		const next: SettingColour|undefined =
			choice=='none' ? undefined :
			choice=='literal' ? {literal:''} :
			carried==0 ? {palette:choice} : {palette:choice, brightnessAdjustment:carried};

		await replaceDoc(sw,next);
	};

	const setValue = (v:string) => {
		if (typeof stored()=='string') {
			sw.setStore(...sw.location,{literal:v});
			return;
		}
		const c = colour();
		if (c!=undefined && 'literal' in c)
			sw.setStore(...sw.location,'literal',v);
	};

	const flushValue = () => { replaceDoc(sw,colour()); };

	const setBrightnessAdjustment = async (raw:string) => {
		const c = colour();
		if (c==undefined || !('palette' in c))
			return;

		const parsed = Number.parseInt(raw);
		const next = Number.isNaN(parsed) ? 0 : Math.min(100,Math.max(-100,parsed));

		await replaceDoc(sw, next==0 ? {palette:c.palette} : {palette:c.palette, brightnessAdjustment:next});
	};

	return (
		<div>
			<div class={rowStyle}>
				<PaletteSourceSelect palette={palette()} selected={selected()} choose={setSource} />

				<Show when={selected()=='literal'}
					fallback={<Swatch colour={colour()} palette={palette()} />}
				>
					<ColourPicker value={literalValue()} onChange={setValue} onClose={flushValue} />
				</Show>

				<Show when={isPaletteColour()}>
					<LabelOnLeftWrap label='Br %' narrow={true}>
						<input type='number' class={percentageStyle()}
							min={-100} max={100}
							value={brightnessAdjustment()}
							onChange={e => setBrightnessAdjustment(e.currentTarget.value)}
						/>
					</LabelOnLeftWrap>
				</Show>
			</div>

			<Show when={P.notes!=''}>
				<div class={notesStyle}>{P.notes}</div>
			</Show>
		</div>
	);
}


/*
	The 'which colour' dropdown. A native <select> can't put a swatch beside an option, so this is a
	hand-built list - trigger plus absolutely-positioned panel - following Shared/forms/DrillDownSelect:
	it closes when focus leaves the wrapper (no document listener, no onMount) and the panel refuses
	mousedown so a row click isn't preceded by a focusout that would close it first.

	'selected' is the current source key ('none', 'literal', or a palette entry name); 'choose' is
	handed that same kind of key back.
*/
function PaletteSourceSelect(P:{palette:Palette, selected:string, choose:(key:string)=>void}): JSX.Element
{
	const [open,setOpen] = createSignal(false);

	const close = () => setOpen(false);

	const take = (key:string) => { close(); P.choose(key); };

	const onFocusOut = (e:FocusEvent & {currentTarget:HTMLDivElement}) => {
		if (!e.currentTarget.contains(e.relatedTarget as Node|null))
			close();
	};

	const onKeyDown = (e:KeyboardEvent) => {
		if (e.key == 'Escape')
			close();
	};

	const toggle = (e:MouseEvent & {currentTarget:HTMLButtonElement}) => {
		e.currentTarget.focus();
		setOpen(!open());
	};

	return (
		<div class={sourceWrapStyle} onFocusOut={onFocusOut} onKeyDown={onKeyDown}>
			<button type='button' class={sourceTriggerStyle} onClick={toggle}>
				<span>{sourceLabel(P.selected)}</span>
				<i class={`fas fa-caret-down ${caretStyle}`} />
			</button>

			<Show when={open()}>
				<div class={sourcePanelStyle} onMouseDown={e => e.preventDefault()}>
					<For each={Object.keys(P.palette)}>{key =>
						<button type='button'
							classList={{[sourceRowStyle]:true, [sourceRowCurrentStyle]: key==P.selected}}
							onClick={() => take(key)}
						>
							<PaletteDot colour={P.palette[key]} />
							<span>{sourceLabel(key)}</span>
						</button>
					}</For>

					<hr />

					<button type='button'
						classList={{[sourceRowStyle]:true, [sourceRowCurrentStyle]: P.selected=='literal'}}
						onClick={() => take('literal')}
					>
						<span class={dotIconStyle}><i class='fas fa-eye-dropper' /></span>
						<span>{sourceLabel('literal')}</span>
					</button>

					<button type='button'
						classList={{[sourceRowStyle]:true, [sourceRowCurrentStyle]: P.selected=='none'}}
						onClick={() => take('none')}
					>
						<span class={dotIconStyle}><i class='fas fa-ban' /></span>
						<span>{sourceLabel('none')}</span>
					</button>
				</div>
			</Show>
		</div>
	);
}


/* A colour circle for one palette entry. An entry the venue has removed (or never set) carries '',
   which shows as a checker rather than a filled dot. */
function PaletteDot(P:{colour:string|undefined}): JSX.Element
{
	return (
		<span
			classList={{[dotStyle]:true, [dotEmptyStyle]: !P.colour}}
			title={P.colour ? P.colour : 'Not set'}
			style={P.colour ? {background: P.colour} : undefined}
		/>
	);
}


/*
	Shows what the setting actually paints - palette link and brightness adjustment included - so the
	effect of pointing at 'feature' and brightening it is visible without loading the frontend.
*/
function Swatch(P:{colour:SettingColour|undefined, palette:Palette}): JSX.Element
{
	const resolved = () => resolveColour(P.colour,P.palette);

	return (
		<div class={swatchStyle} title={resolved()=='' ? 'Not set' : resolved()}>
			<Show when={resolved()!=''}>
				<div class={swatchColourStyle} style={{background: resolved()}} />
			</Show>
		</div>
	);
}
